import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { api } from "../lib/api";

interface JitsiExternalAPI {
  dispose: () => void;
}
interface JitsiAPIConstructor {
  new (domain: string, options: {
    roomName: string;
    parentNode: HTMLElement;
    width: string;
    height: string;
    userInfo: { displayName: string; email: string };
    configOverwrite: Record<string, unknown>;
    interfaceConfigOverwrite: Record<string, unknown>;
  }): JitsiExternalAPI;
}
declare global {
  interface Window {
    JitsiMeetExternalAPI?: JitsiAPIConstructor;
  }
}

interface JitsiCallProps {
  chatId: string;
  mode: "audio" | "video";
  onClose: () => void;
}

export function JitsiCall({ chatId, mode, onClose }: JitsiCallProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const apiRef = useRef<JitsiExternalAPI | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const room = await api.jitsiRoom(chatId);
        const start = () => {
          if (cancelled) return;
          if (!window.JitsiMeetExternalAPI) {
            setTimeout(start, 200);
            return;
          }
          if (!containerRef.current) return;
          const Ctor = window.JitsiMeetExternalAPI;
          apiRef.current = new Ctor(room.domain, {
            roomName: room.room,
            parentNode: containerRef.current,
            width: "100%",
            height: "100%",
            userInfo: { displayName: room.user.name, email: room.user.email },
            configOverwrite: {
              prejoinPageEnabled: false,
              startWithAudioMuted: false,
              startWithVideoMuted: mode === "audio",
              disableModeratorIndicator: false,
              enableLobbyChat: !!room.options.lobby,
            },
            interfaceConfigOverwrite: {
              MOBILE_APP_PROMO: false,
              SHOW_JITSI_WATERMARK: false,
              DEFAULT_BACKGROUND: "#06080d",
              TOOLBAR_BUTTONS: ["microphone", "camera", "desktop", "chat", "raisehand", "tileview", "hangup", "settings"],
            },
          });
        };
        start();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      cancelled = true;
      try { apiRef.current?.dispose(); } catch { /* empty */ }
    };
  }, [chatId, mode]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-2 md:p-6 animate-fade-in" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)" }}>
      <div className="glass-strong w-full h-full max-w-6xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
          <div className="font-display font-semibold">{mode === "video" ? "Видеозвонок" : "Голосовой звонок"} · Jitsi Meet</div>
          <button className="btn btn-ghost p-2 rounded-full" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="flex-1 relative bg-black">
          <div ref={containerRef} className="absolute inset-0" />
          {error && <div className="absolute inset-0 grid place-items-center text-red-300">{error}</div>}
        </div>
      </div>
    </div>
  );
}
